import React, { useEffect, useState } from 'react';
import { Search, Loader2, User, Phone, MapPin, Briefcase, FileText, Download, Globe, Shield, Mail, AlertCircle, CheckCircle, ChevronDown, ChevronRight, Calendar, DollarSign, Car, Heart, Scale, GraduationCap, MessageSquare, Users } from 'lucide-react';
import { supabase } from '../../lib/supabase';

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);

  // Documents state
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [showDocs, setShowDocs] = useState(false);

  // Assign email state
  const [assigningEmail, setAssigningEmail] = useState(false);
  const [assignedEmailForm, setAssignedEmailForm] = useState({ email: '', password: '' });

  // Notification state
  const [notification, setNotification] = useState({ type: '', text: '' });

  // Job Application Profile accordion state
  const [expandedSection, setExpandedSection] = useState(null);
  const toggleSection = (section) => setExpandedSection(expandedSection === section ? null : section);

  const showNotification = (type, text) => {
    setNotification({ type, text });
    setTimeout(() => {
      setNotification(prev => prev.text === text ? { type: '', text: '' } : prev);
    }, 5000);
  };

  useEffect(() => {
    if (selectedUser) {
      setAssignedEmailForm({ 
        email: selectedUser.assigned_email || '', 
        password: selectedUser.assigned_email_password || '' 
      });
    }
  }, [selectedUser]);

  const handleAssignEmail = async () => {
    setAssigningEmail(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ 
          assigned_email: assignedEmailForm.email, 
          assigned_email_password: assignedEmailForm.password 
        })
        .eq('id', selectedUser.id);
      
      if (error) throw error;
      
      const updatedUsers = users.map(u => 
        u.id === selectedUser.id 
          ? { ...u, assigned_email: assignedEmailForm.email, assigned_email_password: assignedEmailForm.password } 
          : u
      );
      setUsers(updatedUsers);
      setSelectedUser({ ...selectedUser, assigned_email: assignedEmailForm.email, assigned_email_password: assignedEmailForm.password });

      // Send secure email notification to the user
      try {
        await supabase.functions.invoke('send-email', {
          body: {
            user_id: selectedUser.id,
            subject: '🔑 Your Dedicated Job Application Account is Ready!',
            html: `
              <div style="font-family: sans-serif; max-width: 600px; margin: 0 auto; color: #334155; padding: 24px; border: 1px solid #e2e8f0; border-radius: 16px;">
                <div style="text-align: center; margin-bottom: 24px;">
                  <span style="font-size: 40px;">🔑</span>
                </div>
                <h2 style="color: #4F46E5; text-align: center; margin-top: 0; margin-bottom: 8px;">Dedicated Account Configured</h2>
                <p style="text-align: center; color: #64748B; font-size: 14px; margin-top: 0; margin-bottom: 24px;">Placement Point Account Management</p>
                
                <p>Hello ${selectedUser.first_name || 'there'},</p>
                <p>Great news! We have successfully configured your <strong>dedicated job application account</strong>. We will use this account to securely search, optimize, and apply for high-value positions on your behalf.</p>
                
                <div style="background-color: #EEF2FF; border-left: 4px solid #4F46E5; padding: 16px; margin: 24px 0; border-radius: 12px;">
                  <h3 style="color: #312E81; margin: 0 0 6px 0; font-size: 15px; font-weight: bold;">🔒 Secure Credential Transfer Policy</h3>
                  <p style="margin: 0; font-size: 13px; color: #4338CA; line-height: 1.5;">
                    To keep your login details completely safe and prevent unauthorized access, we never transmit passwords or sensitive login credentials via email.
                  </p>
                </div>

                <p>You can securely view your new email address and password at any time by logging in to your private, encrypted Placement Point portal dashboard.</p>
                
                <div style="text-align: center; margin: 32px 0;">
                  <a href="${window.location.origin}/dashboard/profile" style="background-color: #4F46E5; color: #ffffff; padding: 14px 28px; text-decoration: none; border-radius: 12px; font-weight: bold; font-size: 14px; display: inline-block; box-shadow: 0 4px 6px -1px rgba(79, 70, 229, 0.2);">View Secure Credentials</a>
                </div>

                <p style="font-size: 13px; color: #64748B; line-height: 1.5;">
                  <strong>Note:</strong> Please do not share your login details with anyone. If you have any questions or need to make updates, reach out to your coach via the portal's message center.
                </p>
                
                <hr style="border: 0; border-top: 1px solid #E2E8F0; margin: 32px 0 24px 0;" />
                <p style="font-size: 12px; color: #94A3B8; text-align: center; margin: 0;">
                  Best regards,<br/>
                  <strong>The Placement Point Team</strong>
                </p>
              </div>
            `
          }
        });
      } catch (emailErr) {
        console.error('Failed to send email notification:', emailErr);
      }

      showNotification('success', 'Account assigned successfully!');
    } catch (err) {
      showNotification('error', 'Failed to assign account: ' + err.message);
    } finally {
      setAssigningEmail(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async (userId) => {
    setLoadingDocs(true);
    setShowDocs(true);
    try {
      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setDocuments([]);
    } finally {
      setLoadingDocs(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const { data, error } = await supabase.storage
        .from('documents')
        .createSignedUrl(doc.file_url, 60);

      if (error) throw error;
      window.open(data.signedUrl, '_blank');
    } catch (err) {
      showNotification('error', 'Failed to download file.');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const filteredUsers = users.filter(user => {
    const fullName = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
    const role = (user.target_role || '').toLowerCase();
    return fullName.includes(searchQuery.toLowerCase()) || role.includes(searchQuery.toLowerCase());
  });

  const formatUserName = (u) => {
    if (!u) return 'Client';
    const fn = (u.first_name || '').trim();
    const ln = (u.last_name || '').trim();
    if (fn && fn !== 'null' && fn !== 'Unknown') {
      return `${fn} ${ln && ln !== 'null' ? ln : ''}`.trim();
    }
    if (u.email) {
      const prefix = u.email.split('@')[0];
      return prefix.charAt(0).toUpperCase() + prefix.slice(1);
    }
    return 'Client';
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-indigo-950">Client Management</h1>
          <p className="mt-1 text-slate-500">View and manage all registered clients.</p>
        </div>
      </div>

      {notification.text && (
        <div className={`p-4 rounded-xl text-sm font-semibold flex items-center gap-2 ${
          notification.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-rose-50 text-rose-800 border border-rose-200'
        }`}>
          {notification.type === 'success' ? <CheckCircle className="w-5 h-5 flex-shrink-0 text-emerald-600" /> : <AlertCircle className="w-5 h-5 flex-shrink-0 text-rose-600" />}
          <span>{notification.text}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Users Table */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-4">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name, email, or role..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
              />
            </div>
            <div className="text-xs font-semibold text-slate-500">
              {filteredUsers.length} Users
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-indigo-600" />
              Loading users...
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase font-bold border-b border-slate-100">
                  <tr>
                    <th className="p-4">User</th>
                    <th className="p-4">Plan</th>
                    <th className="p-4">Target Role</th>
                    <th className="p-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredUsers.map((user) => (
                    <tr
                      key={user.id}
                      className={`hover:bg-slate-50 transition-colors cursor-pointer ${selectedUser?.id === user.id ? 'bg-indigo-50/50' : ''}`}
                      onClick={() => { setSelectedUser(user); setShowDocs(false); setDocuments([]); }}
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          {user.avatar_url ? (
                            <img src={user.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold">
                              {(formatUserName(user)[0] || 'C').toUpperCase()}
                            </div>
                          )}
                          <div>
                            <div className="font-bold text-slate-900">{formatUserName(user)}</div>
                            <div className="text-xs text-slate-500">{user.phone || 'No phone'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 whitespace-nowrap">
                        <span className={`px-2.5 py-1 text-xs font-bold rounded-full capitalize ${
                          user.subscription_status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {(!user.subscription_plan || user.subscription_plan === 'none' || user.subscription_plan === 'placement_fee') ? 'Free' : user.subscription_plan}
                        </span>
                      </td>
                      <td className="p-4 text-slate-600">
                        {user.target_role ? (
                          <span className="truncate block max-w-[150px]">{user.target_role}</span>
                        ) : (
                          <span className="text-slate-400 italic">Not set</span>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        <button className="text-indigo-600 font-semibold text-xs hover:text-indigo-800">
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                  {filteredUsers.length === 0 && (
                    <tr>
                      <td colSpan="4" className="p-8 text-center text-slate-500">
                        No clients found matching your search.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* User Details Sidebar */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          {selectedUser ? (
            <div className="space-y-6">
              <div className="flex flex-col items-center text-center pb-6 border-b border-slate-100">
                {selectedUser.avatar_url ? (
                  <img src={selectedUser.avatar_url} alt="" className="w-20 h-20 rounded-full object-cover mb-4" />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-2xl mb-4">
                    {(formatUserName(selectedUser)[0] || 'C').toUpperCase()}
                  </div>
                )}
                <h3 className="text-xl font-bold text-slate-900">{formatUserName(selectedUser)}</h3>
                <div className="flex items-center gap-2 mt-2">
                  <span className={`px-2.5 py-1 text-xs font-bold rounded-full capitalize ${
                    selectedUser.subscription_status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {(!selectedUser.subscription_plan || selectedUser.subscription_plan === 'none' || selectedUser.subscription_plan === 'placement_fee') ? 'Free' : selectedUser.subscription_plan} Plan
                  </span>
                </div>
              </div>

              <div className="space-y-4 text-sm">
                <div className="flex items-start gap-3">
                  <Phone className="w-4 h-4 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-slate-500 text-xs">Phone</p>
                    <p className="font-medium text-slate-900">{selectedUser.phone || 'Not provided'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Briefcase className="w-4 h-4 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-slate-500 text-xs">Target Roles</p>
                    <p className="font-medium text-slate-900">{selectedUser.target_role || 'Not set'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-slate-500 text-xs">Target Location</p>
                    <p className="font-medium text-slate-900">{selectedUser.target_location || 'Not set'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <User className="w-4 h-4 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-slate-500 text-xs">Visa Requirement</p>
                    <p className="font-medium text-slate-900 capitalize">{selectedUser.visa_status?.replace('_', ' ') || 'Not set'}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Globe className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 overflow-hidden">
                    <p className="text-slate-500 text-xs">Portfolio / Websites</p>
                    <p className="font-medium text-slate-900 break-words whitespace-pre-wrap">{selectedUser.portfolio_url || 'Not provided'}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Globe className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 overflow-hidden">
                    <p className="text-slate-500 text-xs">Social Media</p>
                    <p className="font-medium text-slate-900 break-words whitespace-pre-wrap">{selectedUser.social_media_url || 'Not provided'}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Shield className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-slate-500 text-xs">SSN</p>
                    <p className="font-medium text-slate-900">{selectedUser.ssn || 'Not provided'}</p>
                  </div>
                </div>
              </div>

              {/* ── Job Application Profile (Collapsible) ── */}
              <div className="pt-6 border-t border-slate-100">
                <h4 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-indigo-600" /> Job Application Profile
                </h4>

                {/* Helper row renderer */}
                {(() => {
                  const Row = ({ label, value }) => (
                    <div className="flex justify-between items-start py-1.5 text-xs">
                      <span className="text-slate-500 flex-shrink-0 w-[45%]">{label}</span>
                      <span className="font-medium text-slate-900 text-right break-words whitespace-pre-wrap">{value || <span className="text-slate-300 italic">—</span>}</span>
                    </div>
                  );

                  const Section = ({ id, title, icon: SIcon, children }) => (
                    <div className="border border-slate-100 rounded-xl overflow-hidden mb-2">
                      <button
                        type="button"
                        onClick={() => toggleSection(id)}
                        className="w-full flex items-center gap-2 px-3 py-2.5 text-xs font-bold text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        <SIcon className="w-3.5 h-3.5 text-indigo-500" />
                        <span className="flex-1 text-left">{title}</span>
                        {expandedSection === id ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                      </button>
                      {expandedSection === id && <div className="px-3 pb-3 border-t border-slate-50">{children}</div>}
                    </div>
                  );

                  const u = selectedUser;
                  return (
                    <div>
                      <Section id="personal" title="Personal Details" icon={User}>
                        <Row label="Date of Birth" value={u.date_of_birth} />
                        <Row label="Available From" value={u.available_from} />
                        <Row label="Customer Type" value={u.customer_type} />
                        <Row label="Address" value={u.address} />
                      </Section>

                      <Section id="job" title="Job Preferences" icon={Briefcase}>
                        <Row label="Preferred Jobs" value={u.preferred_jobs} />
                        <Row label="Target Role" value={u.target_role} />
                        <Row label="Experience" value={u.years_of_experience} />
                        <Row label="Employment Type" value={u.employment_type} />
                        <Row label="W2 / 1099" value={u.w2_or_1099} />
                        <Row label="Hourly Wage" value={u.hourly_wage} />
                        <Row label="Annual Salary" value={u.annual_salary} />
                        <Row label="Target Salary" value={u.target_salary} />
                        <Row label="Accept Lower?" value={u.accept_lower_offer} />
                      </Section>

                      <Section id="education" title="Education & Skills" icon={GraduationCap}>
                        <Row label="Education Level" value={u.education_level} />
                        <Row label="Other Education" value={u.other_education} />
                        <Row label="Start Date" value={u.education_start} />
                        <Row label="End Date" value={u.education_end} />
                        <Row label="Languages" value={u.languages} />
                        <Row label="Certifications" value={u.certifications} />
                      </Section>

                      <Section id="location" title="Location & Travel" icon={MapPin}>
                        <Row label="Target Location" value={u.target_location} />
                        <Row label="Authorized in USA" value={u.authorized_in_usa} />
                        <Row label="Visa Status" value={u.visa_status?.replace('_', ' ')} />
                        <Row label="Willing to Travel" value={u.willing_to_travel} />
                        <Row label="International Travel" value={u.international_travel} />
                        <Row label="Work Anywhere USA" value={u.work_anywhere_usa} />
                        <Row label="Preferred State" value={u.preferred_state} />
                        <Row label="Remote Anywhere" value={u.remote_anywhere_usa} />
                      </Section>

                      <Section id="background" title="Background & Legal" icon={Scale}>
                        <Row label="References" value={u.references_list} />
                        <Row label="Driver's License" value={u.drivers_license} />
                        <Row label="Work Weekends" value={u.work_on_weekends} />
                        <Row label="Convicted of Crime" value={u.convicted_of_crime} />
                        {u.convicted_of_crime === 'Yes' && <Row label="Explanation" value={u.crime_explanation} />}
                      </Section>

                      <Section id="eeo" title="EEO Demographics" icon={Users}>
                        <Row label="Gender" value={u.gender} />
                        <Row label="Hispanic / Latino" value={u.hispanic_latino} />
                        <Row label="Race" value={u.race} />
                        <Row label="Veteran Status" value={u.veteran_status} />
                        <Row label="Disability" value={u.disability} />
                      </Section>

                      <Section id="communication" title="Communication" icon={MessageSquare}>
                        <Row label="Referral Source" value={u.referral_source} />
                        <Row label="Do Not Apply To" value={u.do_not_apply_to} />
                        <Row label="Message to Specialist" value={u.message_to_specialist} />
                      </Section>
                    </div>
                  );
                })()}
              </div>

              {/* Dedicated Application Account Section */}
              <div className="pt-6 border-t border-slate-100">
                <h4 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-indigo-600" /> Dedicated Application Account
                </h4>
                <div className="space-y-3 bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Email Address</label>
                    <input 
                      type="email" 
                      value={assignedEmailForm.email}
                      onChange={(e) => setAssignedEmailForm({...assignedEmailForm, email: e.target.value})}
                      placeholder="client@gmail.com"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Password</label>
                    <input 
                      type="text" 
                      value={assignedEmailForm.password}
                      onChange={(e) => setAssignedEmailForm({...assignedEmailForm, password: e.target.value})}
                      placeholder="Password"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>
                  <button 
                    onClick={handleAssignEmail}
                    disabled={assigningEmail}
                    className="w-full py-2 bg-indigo-600 text-white rounded-lg text-sm font-bold hover:bg-indigo-700 transition-colors disabled:opacity-50"
                  >
                    {assigningEmail ? 'Saving...' : 'Save Account Info'}
                  </button>
                </div>
              </div>

              {/* Documents Section */}
              <div className="pt-6 border-t border-slate-100">
                <button 
                  onClick={() => fetchDocuments(selectedUser.id)}
                  className="w-full py-2.5 bg-indigo-50 text-indigo-700 rounded-lg font-bold text-sm hover:bg-indigo-100 transition-colors flex items-center justify-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  {showDocs ? 'Refresh Documents' : 'View Uploaded Documents'}
                </button>

                {showDocs && (
                  <div className="mt-4 space-y-2">
                    {loadingDocs ? (
                      <div className="flex justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin text-indigo-600" />
                      </div>
                    ) : documents.length > 0 ? (
                      documents.map((doc) => (
                        <div key={doc.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                          <div className="w-9 h-9 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <FileText className="w-4 h-4 text-indigo-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">{doc.file_name}</p>
                            <p className="text-[11px] text-slate-400">
                              {formatFileSize(doc.file_size)} · {new Date(doc.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDownload(doc); }}
                            className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                            title="Download"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-4">
                        <FileText className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                        <p className="text-xs text-slate-400">No documents uploaded yet.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center text-slate-500 py-12">
              <User className="w-12 h-12 text-slate-300 mb-3" />
              <p>Select a client from the list to view their full profile.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
